import type { SxProps } from "@mui/material";
import { Divider, ListItemText, MenuItem, Popover, Select, Stack, Typography } from "@mui/material";
import { formatISO, isSameDay, parseISO } from "date-fns";
import { useMemo, useState } from "react";
import useResizeObserver from "@entrilia/shared/hooks/useResizeObserver";
import { ReferenceDateType } from "../../../../../../api/types/dashboardsTypes";
import DateFilterEditor from "../../../../../common/filters/editors/DateFilterEditor";
import type { FilterValue } from "../../../../../common/filters/filterTypes";
import { parseIsoOrLocalFormat } from "@entrilia/shared/components/reporting/bc-datepicker/bcDatePickerHelper";
import { formatToISODate } from "../../../../reporting/utils/dateUtils";
import { currentPeriodDateTypes, getReferenceDateLabel, lastPeriodDateTypes } from "./ReferenceDatePicker.types";
import { getDateLabel } from "./getDateLabel";

const isLongRange = (value: ReferenceDateType) => {
  return value === ReferenceDateType.CustomRange || value === ReferenceDateType.Last12Months;
};

interface Props {
  valueType: ReferenceDateType;
  customValue: string[];
  disabled?: boolean;
  onChange: (valueType: ReferenceDateType, customValue: string[]) => void;
}

const ReferenceDatePicker = ({ valueType, customValue, disabled, onChange }: Props) => {
  const currentValue = valueType || ReferenceDateType.ThisMonth;
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [selectEl, setSelectEl] = useState<HTMLElement | null>(null);
  const [selectWidth, setSelectWidth] = useState(0);

  const customValueWithBrowserLocale = useMemo(() => {
    return customValue.map((value) => formatISO(parseIsoOrLocalFormat(value) ?? value));
  }, [customValue]);

  const showSubLabel =
    (selectWidth >= 220 && !isLongRange(currentValue)) || (selectWidth >= 300 && isLongRange(currentValue));

  const handleOnChange = (value: ReferenceDateType) => {
    onChange(value, []);
    if (value === ReferenceDateType.CustomRange) {
      setShowCustomRange(true);
    }
  };

  useResizeObserver(selectEl, {}, (entries) => {
    const width = entries[0]?.target.clientWidth;
    setSelectWidth(width || 0);
  });

  return (
    <>
      <Select<ReferenceDateType>
        ref={(el: HTMLElement) => setSelectEl(el)}
        value={currentValue}
        onChange={(e) => handleOnChange(e.target.value)}
        disabled={disabled}
        fullWidth
        renderValue={(value) => (
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 1, p: 0 }}>
            <ListItemText sx={{ m: 0 }}>{getReferenceDateLabel(value)}</ListItemText>
            {showSubLabel && (
              <Typography variant="caption" color="textSecondary">
                {getDateLabel(value, customValue)}
              </Typography>
            )}
          </Stack>
        )}
      >
        {currentPeriodDateTypes.map((option) => renderMenuItem(option, []))}
        <Divider />
        {lastPeriodDateTypes.map((option) => renderMenuItem(option, []))}
        <Divider />
        {renderMenuItem(ReferenceDateType.CustomRange, customValue, () => setShowCustomRange(true))}
      </Select>
      <Popover
        open={showCustomRange}
        onClose={() => setShowCustomRange(false)}
        anchorEl={selectEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <DateFilterEditor
          filter={{
            type: "date",
            value: {
              operator: "range",
              range: [customValueWithBrowserLocale[0] || "", customValueWithBrowserLocale[1] || ""],
            },
            id: "custom-range",
            name: "Custom Range",
          }}
          showOperator={false}
          onUpdateValue={(value: FilterValue) => {
            if (value.operator === "range") {
              const dateRange = value.range;
              const [dateFrom, dateTo] = dateRange || ["", ""];
              if (!dateFrom || !dateTo) {
                return;
              }
              const date1 = parseISO(dateFrom);
              const date2 = parseISO(dateTo);
              if (dateRange && isSameDay(date1, date2)) {
                onChange(ReferenceDateType.CustomRange, [formatToISODate(date1)]);
              } else {
                onChange(currentValue, [formatToISODate(date1), formatToISODate(date2)]);
              }
            }
          }}
        />
      </Popover>
    </>
  );
};

const renderMenuItem = (option: ReferenceDateType, customValue: string[], onClick?: () => void, sx?: SxProps) => (
  <MenuItem value={option} onClick={onClick} sx={sx}>
    <ListItemText>{getReferenceDateLabel(option)}</ListItemText>
    <Typography variant="caption" color="textSecondary">
      {getDateLabel(option, customValue)}
    </Typography>
  </MenuItem>
);

export default ReferenceDatePicker;
